import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { getPersonsGuestPreview, getPersonsGuestStoriesPreview, getPersonTags, regeneratePersonImage } from '@/api/query/person';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { isAuthenticated, useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguage } from '@/common/hooks/useLanguageData';
import { matchesCategory } from '../utils/matchesCategory';

export const HOME_PAGE_SIZE = 24;
export const HOME_STORIES_LIMIT = 10;

export const useHomePersons = () => {
	const navigate = useNavigate();
	const { openAuthModal } = useGuestAuth();
	const { trackAnalytics } = useAnalytics();
	const { lang } = useLanguage();
	const [persons, setPersons] = useState([]);
	const [storyPersons, setStoryPersons] = useState([]);
	const [tags, setTags] = useState([]);
	const [selectedProfile, setSelectedProfile] = useState();
	const [activeCategory, setActiveCategory] = useState('all');
	const [skip, setSkip] = useState(0);
	const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
	const [isStoriesFetchDone, setIsStoriesFetchDone] = useState(false);

	const buildPersonQuery = useCallback(
		(skipValue) => ({
			skip: skipValue,
			limit: HOME_PAGE_SIZE,
			language: lang,
		}),
		[lang],
	);

	const reloadPersonMutation = useMutation({
		mutationFn: (payload) => regeneratePersonImage(payload),
		onSuccess: ({ data }) => {
			setPersons((prev) => prev.map((p) => (p._id === data._id ? data : p)));
		},
	});

	const loadTagsMutation = useMutation({
		mutationFn: () => getPersonTags({ language: lang }),
		onSuccess: ({ data }) => setTags(() => data ?? []),
		onError: () => setTags([]),
	});

	const loadPeopleMutation = useMutation({
		mutationFn: (payload) => getPersonsGuestPreview(payload),
		onSuccess: ({ data }, variables) => {
			setPersons((prev) =>
				variables?.skip === 0 ? data : [...prev, ...data],
			);
			if (variables?.skip === 0) {
				setIsInitialFetchDone(true);
			}
		},
		onError: (_error, variables) => {
			if (variables?.skip === 0) {
				setIsInitialFetchDone(true);
			}
		},
	});

	const loadStoriesMutation = useMutation({
		mutationFn: (payload) => getPersonsGuestStoriesPreview(payload),
		onSuccess: ({ data }) => {
			setStoryPersons(data ?? []);
			setIsStoriesFetchDone(true);
		},
		onError: () => {
			setStoryPersons([]);
			setIsStoriesFetchDone(true);
		},
	});

	const filteredPersons = useMemo(
		() => persons.filter((person) => matchesCategory(person, activeCategory)),
		[persons, activeCategory],
	);

	const hasMore =
		persons.length > 0 &&
		persons.length % HOME_PAGE_SIZE === 0 &&
		!loadPeopleMutation.isPending;

	const isStoriesLoading = !isStoriesFetchDone;
	const isInitialLoading = loadPeopleMutation.isPending && skip === 0;
	const isTagsLoading = loadTagsMutation.isPending;

	const handleReloadImage = (profile) => {
		reloadPersonMutation.mutate({ person: profile._id });
	};

	const loadNextPage = () => {
		const newSkip = skip + HOME_PAGE_SIZE;
		setSkip(newSkip);
		loadPeopleMutation.mutate(buildPersonQuery(newSkip));
	};

	const handleCloseProfile = () => {
		setSelectedProfile(undefined);
	};

	const handleCategoryChange = (tagId) => {
		setActiveCategory(tagId);
	};

	const handleSurprise = () => {
		if (!isAuthenticated()) {
			openAuthModal('register');
			return;
		}
		const pool = filteredPersons.length ? filteredPersons : persons;
		if (!pool.length) {
			return;
		}
		const pick = pool[Math.floor(Math.random() * pool.length)];
		trackAnalytics(ANALYTICS_EVENT.HOME.SURPRISE, {
			personId: pick._id ?? null,
			categoryId: activeCategory ?? null,
		});
		navigate(`/chat/${pick._id}`);
	};

	useEffect(() => {
		loadTagsMutation.mutate();
	}, [lang]);

	useEffect(() => {
		setPersons([]);
		setStoryPersons([]);
		setSkip(0);
		setIsInitialFetchDone(false);
		setIsStoriesFetchDone(false);
		loadPeopleMutation.mutate(buildPersonQuery(0));
		loadStoriesMutation.mutate({
			language: lang,
			limit: HOME_STORIES_LIMIT,
		});
	}, [lang]);

	return {
		persons,
		filteredPersons,
		storyPersons,
		tags,
		isTagsLoading,
		selectedProfile,
		setSelectedProfile,
		activeCategory,
		setActiveCategory: handleCategoryChange,
		handleReloadImage,
		handleCloseProfile,
		loadNextPage,
		handleSurprise,
		hasMore,
		isStoriesLoading,
		isInitialLoading,
		isLoadingMore: loadPeopleMutation.isPending,
	};
};
